<template>
	<div class="export-job-history" v-if="shouldShowComponent">
		<div class="history-header">
			<v-icon size="18" color="#666">mdi-history</v-icon>
			<span class="history-title">{{ t('text.exportHistory', 'Export History') }}</span>
			<v-btn
				icon
				size="x-small"
				variant="text"
				@click="loadJobs"
				:loading="loading"
				class="refresh-btn"
			>
				<v-icon size="16">mdi-refresh</v-icon>
			</v-btn>
		</div>

		<!-- Loading state when we have a currentJobId but jobs haven't loaded yet -->
		<div v-if="loading && jobs.length === 0" class="jobs-loading">
			<v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
			<span class="loading-text">{{ t('text.loadingExportStatus', 'Loading export status...') }}</span>
		</div>

		<div class="jobs-list" v-else>
			<div
				v-for="job in displayedJobs"
				:key="job.jobId"
				class="job-item"
				:class="getJobClass(job)"
				@click="selectJob(job)"
			>
				<div class="job-item-content">
					<!-- Status icon -->
					<div class="job-status">
						<v-icon :color="getStatusColor(job.status)" size="18">
							{{ getStatusIcon(job.status) }}
						</v-icon>
					</div>

					<!-- Job info -->
					<div class="job-info">
						<div class="job-header-row">
							<span class="job-date">{{ formatJobDate(job.createdAt) }}</span>
							<!-- Action badge -->
							<v-chip
								v-if="job.statusData?.action"
								:color="getActionColor(job.statusData.action)"
								size="x-small"
								variant="tonal"
								class="action-chip"
							>
								{{ getActionLabel(job.statusData.action) }}
							</v-chip>
							<!-- Sub-state chip for running jobs -->
							<v-chip
								v-if="isJobRunning(job) && job.statusData?.subState"
								:color="getSubStateColor(job.statusData.subState)"
								size="x-small"
								variant="tonal"
								class="sub-state-chip"
							>
								{{ getSubStateLabel(job.statusData.subState) }}
							</v-chip>
						</div>
						<!-- Progress bar for running jobs -->
						<div v-if="isJobRunning(job)" class="job-progress">
							<v-progress-linear
								:model-value="job.statusData?.progress || 0"
								:color="getStatusColor(job.status)"
								height="4"
								rounded
							></v-progress-linear>
							<span class="progress-text">{{ job.statusData?.progress || 0 }}%</span>
						</div>
						<!-- System status for completed/failed jobs -->
						<div class="job-systems" v-else>
							<!-- Content Hub status -->
							<div class="system-status" v-if="job.statusData?.cmExportResult">
								<v-icon
									:color="getSystemColor(job.statusData.cmExportResult)"
									size="12"
								>
									{{ getSystemIcon(job.statusData.cmExportResult) }}
								</v-icon>
								<span class="system-label">CH</span>
							</div>
							<!-- PEAK status (shown when the job actually pushed to PEAK; SP exports run the PEAK leg too when the client has PEAK_SYNC enabled) -->
							<div class="system-status" v-if="job.statusData?.peakExportResult">
								<v-icon
									:color="getSystemColor(job.statusData.peakExportResult)"
									size="12"
								>
									{{ getSystemIcon(job.statusData.peakExportResult) }}
								</v-icon>
								<span class="system-label">PEAK</span>
							</div>
						</div>
					</div>

					<!-- Arrow -->
					<div class="job-arrow">
						<v-icon size="16" color="#999">mdi-chevron-right</v-icon>
					</div>
				</div>
			</div>
		</div>

		<!-- Show more button if there are more jobs on server -->
		<div class="show-more" v-if="hasMore">
			<v-btn
				variant="text"
				size="small"
				@click="loadMoreJobs"
				:loading="loadingMore"
				class="show-more-btn"
			>
				{{ t('text.showMore', 'Show more') }} ({{ totalJobs - jobs.length }})
			</v-btn>
		</div>

		<!-- Job Details Dialog -->
		<v-dialog v-model="showJobDialog" max-width="500">
			<v-card v-if="currentSelectedJob">
				<v-card-title class="dialog-header">
					<span>{{ t('text.exportDetails', 'Export Details') }}</span>
					<v-btn icon variant="text" @click="showJobDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text>
					<!-- Job meta info -->
					<div class="job-meta">
						<div class="meta-row">
							<span class="meta-label">{{ t('text.action', 'Action') }}:</span>
							<v-chip
								v-if="currentSelectedJob.statusData?.action"
								:color="getActionColor(currentSelectedJob.statusData.action)"
								size="small"
								variant="tonal"
							>
								{{ getActionLabel(currentSelectedJob.statusData.action) }}
							</v-chip>
							<span v-else class="muted">{{ t('text.export', 'Export') }}</span>
						</div>
						<div class="meta-row">
							<span class="meta-label">{{ t('text.status', 'Status') }}:</span>
							<v-chip
								:color="getStatusColor(currentSelectedJob.status)"
								size="small"
								variant="tonal"
							>
								{{ getStatusLabel(currentSelectedJob.status) }}
							</v-chip>
							<v-chip
								v-if="isJobRunning(currentSelectedJob) && currentSelectedJob.statusData?.subState"
								:color="getSubStateColor(currentSelectedJob.statusData.subState)"
								size="small"
								variant="tonal"
								class="ml-2"
							>
								{{ getSubStateLabel(currentSelectedJob.statusData.subState) }}
							</v-chip>
						</div>
						<!-- Progress bar for running jobs -->
						<div class="meta-row progress-row" v-if="isJobRunning(currentSelectedJob)">
							<span class="meta-label">{{ t('text.progress', 'Progress') }}:</span>
							<div class="dialog-progress">
								<v-progress-linear
									:model-value="currentSelectedJob.statusData?.progress || 0"
									:color="getStatusColor(currentSelectedJob.status)"
									height="8"
									rounded
								></v-progress-linear>
								<span class="progress-percent">{{ currentSelectedJob.statusData?.progress || 0 }}%</span>
							</div>
						</div>
						<!-- Status message for running jobs -->
						<div class="meta-row" v-if="isJobRunning(currentSelectedJob) && getStatusMessage(currentSelectedJob)">
							<span class="meta-label">{{ t('text.message', 'Message') }}:</span>
							<span class="status-message">{{ getStatusMessage(currentSelectedJob) }}</span>
						</div>
						<div class="meta-row">
							<span class="meta-label">{{ t('text.created', 'Created') }}:</span>
							<span>{{ formatJobDate(currentSelectedJob.createdAt) }}</span>
						</div>
						<div class="meta-row" v-if="currentSelectedJob.createdBy">
							<span class="meta-label">{{ t('text.createdBy', 'Created by') }}:</span>
							<span>{{ currentSelectedJob.createdBy }}</span>
						</div>
					</div>

					<!-- Export results using ExportStatusWidget style -->
					<div class="export-results">
						<!-- Content Hub -->
						<div class="system-result" v-if="currentSelectedJob.statusData?.cmExportResult">
							<div class="system-header">
								<v-icon :color="getSystemColor(currentSelectedJob.statusData.cmExportResult, 'CM')" size="18">
									{{ getSystemIcon(currentSelectedJob.statusData.cmExportResult, 'CM') }}
								</v-icon>
								<span class="system-name">Content Hub</span>
								<v-spacer></v-spacer>
								<v-btn
									v-if="getContentHubUrl(currentSelectedJob)"
									variant="text"
									size="x-small"
									color="primary"
									:href="getContentHubUrl(currentSelectedJob)"
									target="_blank"
									class="ch-link-btn"
								>
									<v-icon size="14" start>mdi-open-in-new</v-icon>
									{{ t('text.viewInContentHub', 'View in CH') }}
								</v-btn>
							</div>
							<div class="system-details">
								<div class="detail-row">
									<span class="detail-label">MYS Export:</span>
									<span :class="['detail-value', currentSelectedJob.statusData.cmExportResult.success ? 'success' : 'error']">
										{{ currentSelectedJob.statusData.cmExportResult.success ? t('text.sent', 'Sent') : t('text.failed', 'Failed') }}
									</span>
									<span v-if="currentSelectedJob.statusData.cmExportResult.mysCompletedAt" class="detail-time">
										{{ formatTime(currentSelectedJob.statusData.cmExportResult.mysCompletedAt) }}
									</span>
								</div>
								<div class="detail-row" v-if="currentSelectedJob.statusData.cmExportResult.externalVerification">
									<span class="detail-label">{{ t('text.externalStatus', 'External Status') }}:</span>
									<span :class="['detail-value', currentSelectedJob.statusData.cmExportResult.externalVerification.verified ? 'success' : 'error']">
										{{ currentSelectedJob.statusData.cmExportResult.externalVerification.verified ? t('text.published', 'Published') : t('text.notPublished', 'Not Published') }}
									</span>
									<span v-if="currentSelectedJob.statusData.cmExportResult.externalVerification.externalTimestamp" class="detail-time">
										{{ formatTime(currentSelectedJob.statusData.cmExportResult.externalVerification.externalTimestamp) }}
									</span>
								</div>
								<div class="detail-row error-note" v-if="currentSelectedJob.statusData.cmExportResult.error">
									<v-icon size="14" color="error">mdi-alert-circle</v-icon>
									<span>{{ currentSelectedJob.statusData.cmExportResult.error }}</span>
								</div>
								<div class="detail-row stale-warning" v-if="hasStaleTimestamp(currentSelectedJob.statusData.cmExportResult, 'CM')">
									<v-icon size="14" color="warning">mdi-alert</v-icon>
									<span>{{ t('text.staleTimestampWarning', 'External timestamp is older than MYS export - data may not be synced') }}</span>
								</div>
							</div>
						</div>

						<!-- PEAK (shown when the job pushed to PEAK; SP exports include this leg when the client has PEAK_SYNC enabled) -->
						<div class="system-result" v-if="currentSelectedJob.statusData?.peakExportResult">
							<div class="system-header">
								<v-icon :color="getSystemColor(currentSelectedJob.statusData.peakExportResult)" size="18">
									{{ getSystemIcon(currentSelectedJob.statusData.peakExportResult) }}
								</v-icon>
								<span class="system-name">PEAK</span>
							</div>
							<div class="system-details">
								<div class="detail-row">
									<span class="detail-label">MYS Export:</span>
									<span :class="['detail-value', currentSelectedJob.statusData.peakExportResult.success ? 'success' : 'error']">
										{{ currentSelectedJob.statusData.peakExportResult.success ? t('text.sent', 'Sent') : t('text.failed', 'Failed') }}
									</span>
									<span v-if="currentSelectedJob.statusData.peakExportResult.mysCompletedAt" class="detail-time">
										{{ formatTime(currentSelectedJob.statusData.peakExportResult.mysCompletedAt) }}
									</span>
								</div>
								<div class="detail-row" v-if="currentSelectedJob.statusData.peakExportResult.externalVerification">
									<span class="detail-label">{{ t('text.externalStatus', 'External Status') }}:</span>
									<span :class="['detail-value', currentSelectedJob.statusData.peakExportResult.externalVerification.verified ? 'success' : 'error']">
										{{ currentSelectedJob.statusData.peakExportResult.externalVerification.verified ? t('text.found', 'Found') : t('text.notFound', 'Not Found') }}
									</span>
									<span v-if="currentSelectedJob.statusData.peakExportResult.externalVerification.externalTimestamp" class="detail-time">
										{{ formatTime(currentSelectedJob.statusData.peakExportResult.externalVerification.externalTimestamp) }}
									</span>
								</div>
								<div class="detail-row error-note" v-if="currentSelectedJob.statusData.peakExportResult.error">
									<v-icon size="14" color="error">mdi-alert-circle</v-icon>
									<span>{{ currentSelectedJob.statusData.peakExportResult.error }}</span>
								</div>
								<div class="detail-row stale-warning" v-if="hasStaleTimestamp(currentSelectedJob.statusData.peakExportResult)">
									<v-icon size="14" color="warning">mdi-alert</v-icon>
									<span>{{ t('text.staleTimestampWarning', 'External timestamp is older than MYS export - data may not be synced') }}</span>
								</div>
								<div class="detail-row muted" v-if="!currentSelectedJob.statusData.peakExportResult.externalVerification && currentSelectedJob.statusData.peakExportResult.success">
									<span>{{ t('text.verificationNotAvailable', 'External verification not available') }}</span>
								</div>
							</div>
						</div>
					</div>
				</v-card-text>
				<v-card-actions>
					<v-btn
						v-if="canRetryJob(currentSelectedJob)"
						color="warning"
						variant="outlined"
						size="small"
						@click="handleRetry(currentSelectedJob)"
					>
						<v-icon start size="16">mdi-restart</v-icon>
						{{ getRetryButtonLabel(currentSelectedJob) }}
					</v-btn>
					<v-spacer></v-spacer>
					<v-btn variant="text" @click="showJobDialog = false">
						{{ t('text.close', 'Close') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import Common from '@/mixins/Common.vue'
import BackgroundJobExecutive from '../../../../api2/src/modules/jobs/BackgroundJobExecutive'

export default {
	name: 'ExportJobHistory',
	mixins: [Common],
	props: {
		// Generic entity ID - can be productId or serviceProviderId
		entityId: {
			type: String,
			required: true
		},
		// Legacy prop for backwards compatibility with product exports
		productId: {
			type: String,
			default: null
		},
		clientId: {
			type: String,
			required: true
		},
		// Entity type: 'product' (default) or 'serviceProvider'
		entityType: {
			type: String,
			default: 'product',
			validator: (value) => ['product', 'serviceProvider'].includes(value)
		},
		maxDisplayed: {
			type: Number,
			default: 3
		},
		autoRefresh: {
			type: Boolean,
			default: true
		},
		currentJobId: {
			type: String,
			default: null
		}
	},
	emits: ['job-selected', 'retry', 'complete'],

	data() {
		return {
			jobs: [],
			loading: false,
			loadingMore: false,
			totalJobs: 0,
			hasMore: false,
			showJobDialog: false,
			currentSelectedJobId: null, // Store ID instead of object so we can sync with polling updates
			backgroundJobExecutive: new BackgroundJobExecutive(this),
			refreshInterval: null,
			// Pagination constant - number of jobs to fetch on "load more"
			loadMoreLimit: 10,
		}
	},

	computed: {
		// Use maxDisplayed prop as initial limit for server-side pagination
		initialLimit() {
			return this.maxDisplayed
		},
		// Effective entity ID - uses entityId or falls back to productId for backwards compatibility
		effectiveEntityId() {
			return this.entityId || this.productId
		},
		// Whether this is a service provider export. Used to route to the SP-specific
		// job listing endpoint. SP exports DO include the PEAK leg when the client has
		// PEAK_SYNC enabled — PEAK rendering is now gated only by peakExportResult presence.
		isServiceProviderExport() {
			return this.entityType === 'serviceProvider'
		},
		// Show component if we have jobs OR if a currentJobId is provided (job may still be loading)
		shouldShowComponent() {
			return (this.jobs && this.jobs.length > 0) || this.currentJobId
		},
		// Jobs are already sorted by createdAt DESC from server and limited by pagination
		displayedJobs() {
			return this.jobs
		},
		hasRunningJob() {
			return this.jobs.some(job => job.status === 'NEW' || job.status === 'RUNNING')
		},
		// Get the current selected job from the jobs array (keeps it in sync during polling)
		currentSelectedJob() {
			if (!this.currentSelectedJobId) return null
			return this.jobs.find(j => j.jobId === this.currentSelectedJobId) || null
		}
	},

	watch: {
		effectiveEntityId: {
			immediate: true,
			handler() {
				this.loadJobs()
			}
		},
		currentJobId: {
			immediate: true,
			async handler(newJobId, oldJobId) {
				if (newJobId && newJobId !== oldJobId) {
					// New job started - force reload and start polling
					// Add small delay to allow job to be created in DB
					await this.delay(500)
					await this.loadJobs()
					// Always start polling when a new job ID is provided
					// (the job might still be NEW/RUNNING even if loadJobs returns empty initially)
					this.startPolling()
				}
			}
		},
		hasRunningJob(isRunning, wasRunning) {
			if (isRunning && this.autoRefresh) {
				this.startPolling()
			} else if (!isRunning && !this.currentJobId) {
				// Only stop polling if no running jobs AND no currentJobId is set
				// (if currentJobId is set, keep polling in case job hasn't been created yet)
				this.stopPolling()
			}
			// Emit complete when job finishes
			if (wasRunning && !isRunning) {
				const currentJob = this.jobs.find(j => j.jobId === this.currentJobId)
				if (currentJob) {
					this.$emit('complete', currentJob)
				}
			}
		}
	},

	methods: {
		// Translation helper with fallback
		t(key, fallback) {
			const translated = this.$t(key)
			return translated === key || translated.startsWith('text.') ? fallback : translated
		},

		async loadJobs() {
			if (!this.effectiveEntityId || !this.clientId) return

			this.loading = true
			try {
				// Use paginated API - only fetch initialLimit jobs initially
				let result
				if (this.isServiceProviderExport) {
					result = await this.backgroundJobExecutive.getExportServiceProviderJobsPaginated(
						this.effectiveEntityId,
						this.clientId,
						this.initialLimit,
						0
					)
				} else {
					result = await this.backgroundJobExecutive.getExportProductJobsPaginated(
						this.effectiveEntityId,
						this.clientId,
						this.initialLimit,
						0
					)
				}

				this.jobs = result.jobs
				this.totalJobs = result.total
				this.hasMore = result.hasMore

				// Check if the current job has finished - if so, stop polling
				if (this.currentJobId && !this.hasRunningJob) {
					const currentJob = this.jobs.find(j => j.jobId === this.currentJobId)
					if (currentJob && (currentJob.status === 'DONE' || currentJob.status === 'ERROR')) {
						this.stopPolling()
					}
				}
			} catch (error) {
				console.error('Failed to load export jobs:', error)
			} finally {
				this.loading = false
			}
		},

		async loadMoreJobs() {
			if (!this.effectiveEntityId || !this.clientId || this.loadingMore) return

			this.loadingMore = true
			try {
				const offset = this.jobs.length
				let result
				if (this.isServiceProviderExport) {
					result = await this.backgroundJobExecutive.getExportServiceProviderJobsPaginated(
						this.effectiveEntityId,
						this.clientId,
						this.loadMoreLimit,
						offset
					)
				} else {
					result = await this.backgroundJobExecutive.getExportProductJobsPaginated(
						this.effectiveEntityId,
						this.clientId,
						this.loadMoreLimit,
						offset
					)
				}

				// Append new jobs to existing list
				this.jobs = [...this.jobs, ...result.jobs]
				this.totalJobs = result.total
				this.hasMore = result.hasMore
			} catch (error) {
				console.error('Failed to load more export jobs:', error)
			} finally {
				this.loadingMore = false
			}
		},

		startPolling() {
			if (this.refreshInterval) return
			this.refreshInterval = setInterval(() => {
				// When polling, refresh with current page size to preserve expanded state
				this.refreshJobs()
			}, 3000)
		},

		async refreshJobs() {
			// Refresh jobs preserving the current expanded state
			if (!this.effectiveEntityId || !this.clientId) return

			try {
				// Fetch at least as many jobs as we currently have loaded
				const limit = Math.max(this.jobs.length, this.initialLimit)
				let result
				if (this.isServiceProviderExport) {
					result = await this.backgroundJobExecutive.getExportServiceProviderJobsPaginated(
						this.effectiveEntityId,
						this.clientId,
						limit,
						0
					)
				} else {
					result = await this.backgroundJobExecutive.getExportProductJobsPaginated(
						this.effectiveEntityId,
						this.clientId,
						limit,
						0
					)
				}

				this.jobs = result.jobs
				this.totalJobs = result.total
				this.hasMore = result.hasMore

				// Check if the current job has finished - if so, stop polling
				if (this.currentJobId && !this.hasRunningJob) {
					const currentJob = this.jobs.find(j => j.jobId === this.currentJobId)
					if (currentJob && (currentJob.status === 'DONE' || currentJob.status === 'ERROR')) {
						this.stopPolling()
					}
				}
			} catch (error) {
				console.error('Failed to refresh export jobs:', error)
			}
		},

		stopPolling() {
			if (this.refreshInterval) {
				clearInterval(this.refreshInterval)
				this.refreshInterval = null
			}
		},

		delay(ms) {
			return new Promise(resolve => setTimeout(resolve, ms))
		},

		selectJob(job) {
			this.currentSelectedJobId = job.jobId
			this.showJobDialog = true
			this.$emit('job-selected', job)
		},

		getJobClass(job) {
			return {
				'running': job.status === 'NEW' || job.status === 'RUNNING',
				'completed': job.status === 'DONE',
				'failed': job.status === 'ERROR',
			}
		},

		isJobRunning(job) {
			return job.status === 'NEW' || job.status === 'RUNNING'
		},

		getSubStateColor(subState) {
			const colors = {
				'SendingToCM': 'blue',
				'PollingCM': 'blue',
				'CMSynced': 'green',
				'CMFailed': 'red',
				'SendingToPEAK': 'orange',
				'PollingPEAK': 'orange',
				'PEAKSynced': 'green',
				'PEAKFailed': 'red'
			}
			return colors[subState] || 'grey'
		},

		getSubStateLabel(subState) {
			const labels = {
				'SendingToCM': 'Content Hub',
				'PollingCM': 'Verifying CH',
				'CMSynced': 'CH Done',
				'CMFailed': 'CH Failed',
				'SendingToPEAK': 'PEAK',
				'PollingPEAK': 'Verifying PEAK',
				'PEAKSynced': 'PEAK Done',
				'PEAKFailed': 'PEAK Failed'
			}
			return labels[subState] || subState
		},

		getStatusColor(status) {
			switch (status) {
				case 'NEW': return '#2196f3'
				case 'RUNNING': return '#ff9800'
				case 'DONE': return '#4caf50'
				case 'ERROR': return '#f44336'
				default: return '#9e9e9e'
			}
		},

		getStatusIcon(status) {
			switch (status) {
				case 'NEW': return 'mdi-clock-outline'
				case 'RUNNING': return 'mdi-loading mdi-spin'
				case 'DONE': return 'mdi-check-circle'
				case 'ERROR': return 'mdi-alert-circle'
				default: return 'mdi-help-circle'
			}
		},

		getStatusLabel(status) {
			switch (status) {
				case 'NEW': return this.t('text.pending', 'Pending')
				case 'RUNNING': return this.t('text.running', 'Running')
				case 'DONE': return this.t('text.completed', 'Completed')
				case 'ERROR': return this.t('text.failed', 'Failed')
				default: return status
			}
		},

		getActionColor(action) {
			const colors = {
				'approve': 'green',
				'update': 'blue',
				'deactivate': 'orange',
				'expire': 'purple',
				'archive': 'grey',
				'delete': 'red'
			}
			return colors[action] || 'grey'
		},

		getActionLabel(action) {
			const labels = {
				'approve': this.t('text.actionApprove', 'Approval'),
				'update': this.t('text.actionUpdate', 'Update'),
				'deactivate': this.t('text.actionDeactivate', 'Deactivation'),
				'expire': this.t('text.actionExpire', 'Expiration'),
				'archive': this.t('text.actionArchive', 'Archive'),
				'delete': this.t('text.actionDelete', 'Deletion')
			}
			return labels[action] || action
		},

		getSystemColor(result, system) {
			if (!result) return 'grey'
			if (!result.success) return '#f44336'
			if (result.externalVerification && !result.externalVerification.verified) return '#ff9800'
			// Stale timestamp = warning (orange)
			if (this.hasStaleTimestamp(result, system)) return '#ff9800'
			if (result.externalVerification?.verified) return '#4caf50'
			return '#8bc34a'
		},

		getSystemIcon(result, system) {
			if (!result) return 'mdi-circle-outline'
			if (!result.success) return 'mdi-alert-circle'
			if (result.externalVerification && !result.externalVerification.verified) return 'mdi-alert'
			// Stale timestamp = warning icon
			if (this.hasStaleTimestamp(result, system)) return 'mdi-alert'
			if (result.externalVerification?.verified) return 'mdi-check-circle'
			return 'mdi-check-circle-outline'
		},

		hasStaleTimestamp(result, system) {
			if (system === 'CM' && this.currentSelectedJob?.configData?.suppressCmStaleTimestampWarning === true) return false
			return result?.externalVerification?.staleTimestamp === true
		},

		getStatusMessage(job) {
			const message = job?.statusData?.message
			if (!message) return ''
			return message[this.selectedLocale] || message.de || message.en || ''
		},

		getFailedTarget(job) {
			const subState = job?.statusData?.subState
			if (subState === 'CMFailed') return 'CM'
			if (subState === 'PEAKFailed') return 'PEAK'
			return null
		},

		canRetryJob(job) {
			// Retry restarts the whole job with the same configData. Only surfaces
			// when status === 'ERROR' (CMFailed for products/SPs, PEAKFailed for
			// products — SP PEAKFailed is a partial-success terminal state and
			// doesn't flip status to ERROR by design).
			return job?.status === 'ERROR' && this.getFailedTarget(job)
		},

		getRetryButtonLabel(job) {
			const target = this.getFailedTarget(job)
			return target === 'CM'
				? this.t('text.retryContentHub', 'Retry Content Hub')
				: this.t('text.retryPeak', 'Retry PEAK')
		},

		handleRetry(job) {
			const failedTarget = this.getFailedTarget(job)
			if (failedTarget && job?.jobId) {
				this.showJobDialog = false
				this.$emit('retry', failedTarget, job.jobId)
			}
		},

		formatJobDate(dateString) {
			if (!dateString) return ''
			return new Date(dateString).toLocaleString('de-DE', {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
				hour12: false
			})
		},

		formatTime(dateString) {
			if (!dateString) return ''
			return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
		},

		/**
		 * Generate Content Hub URL for a job.
		 * URL format: https://editor.contenthub.dev/spaces/{spaceId}/environments/{environmentId}/entries/MYS_{entityId}
		 */
		getContentHubUrl(job) {
			if (!job?.configData) return null

			const { contentfulSpaceId, contentfulEnvironmentId, productId, serviceProviderId } = job.configData

			// Need spaceId and environmentId
			if (!contentfulSpaceId || !contentfulEnvironmentId) return null

			// Get the entity ID (product or service provider)
			const entityId = productId || serviceProviderId
			if (!entityId) return null

			return `https://editor.contenthub.dev/spaces/${contentfulSpaceId}/environments/${contentfulEnvironmentId}/entries/MYS_${entityId}`
		}
	},

	mounted() {
		if (this.hasRunningJob && this.autoRefresh) {
			this.startPolling()
		}
	},

	beforeUnmount() {
		this.stopPolling()
	}
}
</script>

<style scoped>
.export-job-history {
	background: white;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	padding: 12px;
}

.history-header {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 12px;
	padding-bottom: 8px;
	border-bottom: 1px solid #eee;
}

.history-title {
	font-weight: 500;
	font-size: 13px;
	color: #333;
	flex: 1;
}

.refresh-btn {
	opacity: 0.6;
}

.refresh-btn:hover {
	opacity: 1;
}

.jobs-list {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.job-item {
	background: #f8f9fa;
	border: 1px solid #e8e8e8;
	border-radius: 6px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.job-item:hover {
	border-color: #ccc;
	background: #f0f0f0;
}

.job-item.running {
	border-left: 3px solid #ff9800;
	background: #fff8e1;
}

.job-item.completed {
	border-left: 3px solid #4caf50;
}

.job-item.failed {
	border-left: 3px solid #f44336;
}

.job-item-content {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px;
}

.job-status {
	display: flex;
	align-items: center;
}

.job-info {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.job-header-row {
	display: flex;
	align-items: center;
	gap: 8px;
}

.job-date {
	font-size: 12px;
	color: #333;
	font-weight: 500;
}

.action-chip {
	font-size: 9px !important;
	height: 18px !important;
	text-transform: capitalize;
}

.sub-state-chip {
	font-size: 9px !important;
	height: 18px !important;
}

.job-progress {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 4px;
}

.job-progress .v-progress-linear {
	flex: 1;
}

.progress-text {
	font-size: 10px;
	color: #666;
	min-width: 28px;
	text-align: right;
}

.job-systems {
	display: flex;
	gap: 12px;
}

.system-status {
	display: flex;
	align-items: center;
	gap: 4px;
}

.system-label {
	font-size: 10px;
	color: #666;
	font-weight: 500;
}

.job-arrow {
	display: flex;
	align-items: center;
}

.show-more {
	margin-top: 8px;
	text-align: center;
}

.show-more-btn {
	font-size: 12px;
	color: #666;
}

/* Dialog styles */
.dialog-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 16px !important;
	border-bottom: 1px solid #eee;
}

.job-meta {
	margin-bottom: 16px;
	padding-bottom: 16px;
	border-bottom: 1px solid #eee;
}

.meta-row {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 8px;
}

.meta-label {
	font-size: 12px;
	color: #666;
	min-width: 80px;
}

.progress-row {
	align-items: center;
}

.dialog-progress {
	display: flex;
	align-items: center;
	gap: 12px;
	flex: 1;
}

.dialog-progress .v-progress-linear {
	flex: 1;
}

.progress-percent {
	font-size: 12px;
	font-weight: 500;
	color: #333;
	min-width: 36px;
}

.status-message {
	font-size: 12px;
	color: #555;
}

.export-results {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.system-result {
	background: #f8f9fa;
	border-radius: 6px;
	padding: 12px;
}

.system-header {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 10px;
}

.system-name {
	font-weight: 600;
	font-size: 13px;
}

.system-details {
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin-left: 26px;
}

.detail-row {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 12px;
}

.detail-label {
	color: #666;
	min-width: 90px;
}

.detail-value {
	font-weight: 500;
}

.detail-value.success {
	color: #4caf50;
}

.detail-value.error {
	color: #f44336;
}

.detail-time {
	color: #888;
	font-size: 11px;
	margin-left: auto;
}

.error-note {
	color: #f44336;
	font-size: 11px;
	margin-top: 4px;
}

.stale-warning {
	color: #ff9800;
	font-size: 11px;
	margin-top: 4px;
	background: #fff8e1;
	padding: 6px 8px;
	border-radius: 4px;
	border-left: 3px solid #ff9800;
}

.muted {
	color: #999;
	font-style: italic;
	font-size: 11px;
}

.ch-link-btn {
	font-size: 11px !important;
	text-transform: none;
	padding: 0 8px !important;
}

.jobs-loading {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	padding: 20px;
	color: #666;
}

.loading-text {
	font-size: 13px;
}

@keyframes spin {
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
}

.mdi-spin {
	animation: spin 1s linear infinite;
}
</style>
